import { enableProdMode, importProvidersFrom, provideZonelessChangeDetection } from "@angular/core";
import { environment } from "./environments/environment";
import { PcpStreamingModule, STREAMING_SERVICE_URL } from "@pcp/streaming";
import { HTTP_SERVICE_URL, PcpHttpModule } from "@pcp/http";
import { PCP_API_URL } from "live-lesson/shared/data-access-auth";
import { of } from "rxjs";
import { ImageQuality } from "@pcp/core/interfaces";
import { CommonModule } from "@angular/common";
import { ClassroomConfig, PCP_CLASSROOM_SYNC_CONFIG } from "shared/classroom/data-access";
import { LESSON_YJS_CLIENT_TYPE } from "shared/classroom/data-access";
import { BROWSER_EXTENSION_REQUEST_TIMEOUT, SharedUtilBrowserExtensionModule } from "shared/util-browser-extension";
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { bootstrapApplication, BrowserModule } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { appRoutes } from "./app/app.routes";
import { StoreModule } from "@ngrx/store";
import { EffectsModule } from "@ngrx/effects";
import { provideStoreDevtools, StoreDevtoolsModule } from "@ngrx/store-devtools";
import { LoginModule } from "./app/login/login.module";
import { PCP_SHARED_PREFERRED_IMAGE_QUALITY, provideAppConfig, provideDebugConfig } from "shared/util-runtime-config";
import { LogLevel, provideLogging, parseLogLevel } from "@pcp/logging";
import { provideInsights } from "@pcp/insights";
import { version } from "./environments/version";
import { PcpAuthModule } from "@pcp/auth";
import { SharedClassroomPreviewScreenFeatureShellModule } from "shared/classroom/preview-screen/feature-shell";
import { AppComponent } from "./app/app.component";
import { SharedDevtoolsFeatureShellModule } from "shared/devtools/feature-shell";
import { SharedServiceWorkerFeatureShellModule } from "shared/service-worker/feature-shell";
import { provideSharedClassroomNotificationsConfig } from "shared/classroom/data-access";
import { provideRouter, withDebugTracing } from "@angular/router";
import { LiveLessonModeratorFeatureShellModule } from "live-lesson/moderator/feature-shell";
import { providePcpMatIcons } from "shared/ui/icons";
import { TranslocoRootModule } from "shared/i18n";

if (environment.production) {
    enableProdMode();
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZonelessChangeDetection(),
        provideLogging({
            consoleLevel: parseLogLevel(environment.console_log_level, LogLevel.WARNING, 'console_log_level'),
            telemetryLevel: parseLogLevel(environment.telemetry_log_level, LogLevel.INFO, 'telemetry_log_level'),
        }),
        // Supplies PCP_TELEMETRY_SINK, the destination the logger sends to. Without it the
        // logger writes to the console and sends nothing. Must stay outside
        // importProvidersFrom because it returns EnvironmentProviders. Its position relative
        // to provideLogging() does NOT matter — the two define disjoint tokens and the sink is
        // resolved when the logger is first injected, not at provider-array position.
        provideInsights({
            connectionString: environment.app_insights_connection_string,
            cloudRoleName: environment.app_insights_cloud_role_name,
            applicationVersion: version.build_date,
            logNgrxActions: false,
        }),
        importProvidersFrom(
            CommonModule,
            BrowserModule,
            StoreModule.forRoot({}),
            EffectsModule.forRoot([]),
            LoginModule,
            PcpStreamingModule,
            SharedUtilBrowserExtensionModule.forRoot(500, ""),

            SharedDevtoolsFeatureShellModule.forRoot(),
            SharedServiceWorkerFeatureShellModule.forRoot({
                featureWebPushEnabled: false,
                swEnabled: false,
                swUpdateCheckInterval: 6 * 60 * 60 * 1000,
                vapidPublicKey: '',
            }),

            PcpHttpModule.forRoot(environment.apiUrl),
            PcpAuthModule.forLiveLesson({ tokenStorageAdapter: sessionStorage }),
            SharedClassroomPreviewScreenFeatureShellModule.forPcpLiveLessonModerator({
                votingElementBaseUrl: environment.voting_question_element_base_src,
                difficultQuestionElementBaseUrl: environment.difficult_question_element_base_src,
                htmlElementBaseUrl: environment.blob_url,
            }),
            LiveLessonModeratorFeatureShellModule,
            TranslocoRootModule,
        ),
        provideAppConfig({
            serverUrl: environment.apiUrl,
            publicBlobUrl: environment.blob_url + '/public',
            isProduction: environment.production,
        }),
        provideDebugConfig(),
        { provide: STREAMING_SERVICE_URL, useValue: environment.streaming },
        { provide: HTTP_SERVICE_URL, useValue: environment.apiUrl },
        { provide: PCP_API_URL, useValue: environment.apiUrl },
        // Slide-canvas background variant + backing store: follower display apps always render
        // HIGH, overriding the LOW default from provideAppConfig.
        { provide: PCP_SHARED_PREFERRED_IMAGE_QUALITY, useValue: of(ImageQuality.HIGH) },
        {
            provide: PCP_CLASSROOM_SYNC_CONFIG,
            useValue: {
                isConsumer: true,
                isPresentationScreen: true,
                isEmitter: false,
                emitSlideMutations: false,
                emitHotspotClicks: false,
                emitUndoRedo: false,
                emitHtmlElementModifications: false,
                emitVideoChanges: false,
                emitVideoLoaded: false,
                consumeVideoChanges: true,
                showToolbar: false,
                disableElementUsage: true,
            } as ClassroomConfig,
        },
        { provide: LESSON_YJS_CLIENT_TYPE, useValue: 'LiveLessonModerator' },
        { provide: BROWSER_EXTENSION_REQUEST_TIMEOUT, useValue: 500 },
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        provideRouter(
            appRoutes,
            withDebugTracing()
        ),
        provideSharedClassroomNotificationsConfig({ showSnackbarOnJoinLessonFailure: false }),
        provideStoreDevtools({
            maxAge: 25,
            logOnly: environment.production,
            name: `NgrxStore Live-Session`,
            autoPause: true,
            connectInZone: true,
        }),
        providePcpMatIcons(),
    ],
})
    .catch((err) => console.error(err));
